import type { ColorGradingFilter } from 'js/src/client';
import { ColorGradingFilterStatusEnum } from 'js/src/client';
import { loadColorGradingFilterList } from 'js/src/data/color-grading/color-grading-operations';
import { getColorGradingFilters } from 'js/src/data/color-grading/color-grading-selectors';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

/**
 * The colour grading filter plumbing shared by the tools that take a filter by name
 * (set_global_color_grading for the whole video, set_media_effects for one asset): which filters are
 * offered, how they are described to the model, and how a name is resolved at call time.
 */

export const activeColorGradingFilters = (
    filters: ColorGradingFilter[] | undefined,
): ColorGradingFilter[] =>
    (filters ?? []).filter((filter) => filter.status === ColorGradingFilterStatusEnum.ACTIVE);

export type ColorGradingFilterParameter = { enum?: string[]; description: string };

export function colorGradingFilterParameter(
    filters: ColorGradingFilter[],
): ColorGradingFilterParameter {
    const whatEachFilterLooksLike = filters
        .filter((filter) => filter.description)
        .map((filter) => `${filter.name}: ${filter.description}`)
        .join('\n');
    return {
        ...(filters.length > 0 ? { enum: filters.map((filter) => filter.name) } : {}),
        description: whatEachFilterLooksLike
            ? `The filter to apply, and what each one looks like:\n${whatEachFilterLooksLike}`
            : 'Exact name of a color grading filter.',
    };
}

export async function loadActiveColorGradingFilters(
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<ColorGradingFilter[]> {
    if (getColorGradingFilters(getState()).length === 0) {
        await dispatch(loadColorGradingFilterList());
    }
    return activeColorGradingFilters(getColorGradingFilters(getState()));
}

/** Case-insensitive lookup, since the model often lowercases a name it was shown capitalised. */
export const findColorGradingFilterByName = (
    filters: ColorGradingFilter[],
    name: string,
): ColorGradingFilter | undefined =>
    filters.find((filter) => filter.name.toLowerCase() === name.toLowerCase());

export const listColorGradingFilterNames = (filters: ColorGradingFilter[]): string =>
    filters.map((filter) => filter.name).join(', ');

export const unknownColorGradingFilterError = (
    name: string,
    filters: ColorGradingFilter[],
): string =>
    `Error: Filter "${name}" does not exist. Pick one of these exact names instead: ${listColorGradingFilterNames(filters)}.`;
